import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { debounce } from 'lodash';
import { Collapse, Fade } from '@mui/material';
import { validate as isValidEmail } from 'email-validator';
import { Pathname } from 'Routes';
import { Button, Snackbar } from 'Components';
import { AuthContext, AppContext } from 'Context';
import { AxiosCancelToken, dataFetcher, endpoints } from 'Api';
import { Input } from '../../public/authentication/components/input-field';
import { AuthPage } from '../../public/authentication/components/page';
import styles from './index.module.css';
import authStyles from '../../public/authentication/auth.module.css';

const transitionTimeout = 300;
const defaultSnackbar = { isVisible: false, type: undefined, message: undefined };

export const ManageDevice = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoCode = searchParams.get('code');
  const isAutoMode = Boolean(autoCode);

  const { preferredEmail, setPreferredEmail } = useContext(AuthContext);
  const { langText } = useContext(AppContext);
  const manageDeviceText = langText?.manageDevice;
  const [email, setEmail] = useState(isValidEmail(preferredEmail) ? preferredEmail : '');
  const [qrCode, setQrCode] = useState(autoCode || '');
  const [qrCodeIsValid, setQrCodeValidity] = useState(Boolean(autoCode && autoCode.length >= 6));
  const [emailIsFixed, fixEmail] = useState(false);
  const [emailIsValid, setEmailValidity] = useState(false);
  const [accountExists, setAccountExistence] = useState(undefined);
  const [checkingAccountExistence, setAccountExistenceCheckingStatus] = useState(false);
  const [cancelToken, setCancelToken] = useState(undefined);
  const redirectedRef = useRef(false);

  // A TV code in the URL (?code=…) is NOT linked here any more — opening the
  // link used to be enough to attach a TV to the account. Send the user to the
  // consent screen, which asks them to Allow or Decline first.
  useEffect(() => {
    if (!isAutoMode || redirectedRef.current) return;
    redirectedRef.current = true;
    navigate(`${Pathname.tvLoginApprove}?code=${encodeURIComponent(autoCode)}`, { replace: true });
  }, [isAutoMode, autoCode]);

  const [snackbar, setSnackbar] = useState(defaultSnackbar);

  const { isValidating: addingDevices, mutate: addDevice } = useSWR([endpoints.registerTv, qrCode], {
    fetcher: (url, inputQRCode) => dataFetcher(url, { code: inputQRCode }),
    onSuccess: ({ success, message }) => {
      if (success) {
        setSnackbar({
          isVisible: true,
          message: 'Device Added',
        });
        setTimeout(() => { navigate(Pathname.dashboard); }, 500);
      } else {
        setSnackbar({ isVisible: true, type: 'error', message });
      }
    },
    onError: () => {
      setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while sending Reset Code' });
    },
  });

  const qrCodeHandler = debounce(({ target: { value: inputQrCode } }) => {
    if (inputQrCode.length >= 6) {
      setQrCode(inputQrCode);
      setQrCodeValidity(true);
    } else {
      setQrCode('');
      setQrCodeValidity(false);
    }
  }, 750, { trailing: true });

  // Redirecting to the consent screen — don't flash the code form.
  if (isAutoMode) return null;

  return (
    <AuthPage>
      <form onSubmit={(e) => { e.preventDefault(); }}>
        <Input
          defaultValue={email}
          type="code"
          // isDisabled={(email === preferredEmail) && checkingAccountExistence}
          isLoading={checkingAccountExistence}
          label="Enter TV Code"
          onChange={(e) => {
            e.persist();
            qrCodeHandler(e);
            setAccountExistenceCheckingStatus(false);
            if (cancelToken) { cancelToken.cancel(); }
            if (!e?.target?.value) {
              setEmail('');
              setAccountExistence(undefined);
              setEmailValidity(false);
            } else {
              setCancelToken(AxiosCancelToken.source());
            }
          }}
          placeholder="TV Code"
        />
        <br />
        <Button
          blockText
          isBlue
          isDisabled={!qrCodeIsValid || addingDevices}
          isFullWidth
          isLarge
          onClick={addDevice}
        >
          {addingDevices ? `${(manageDeviceText?.addingDevice || 'Adding Device')}...` : (manageDeviceText?.addDevice || 'Add Device')}
        </Button>
      </form>

      <br />
      <br />
      <br />
      <br />

      <Snackbar
        isVisible={snackbar?.isVisible}
        message={snackbar?.message || ''}
        onClose={() => { setSnackbar(defaultSnackbar); }}
        type={snackbar?.type}
      />
    </AuthPage>
  );
};
